/**
 * Sticky CTA bar — buybox-only sync, scroll visibility, mode resolution.
 * No header coupling.
 */

(function initCtrStickyCtaBar() {
  if (window.__ctrStickyCtaBarInitialized) return;
  window.__ctrStickyCtaBarInitialized = true;

  const BUYBOX_ROOTS = [
    "[data-ctr-pdp-ag1-buybox]",
    "#ctr-pdp-product-buybox-v2-ag1",
    "[data-ctr-pdp-buybox]",
    "#ctr-pdp-product-buybox",
    "#ctr-lp-product-buybox",
    ".ctr-lp-product-buybox",
    "#ctr-everyday-dose-lp-product-buybox",
    "#ctr-ih-lp-product-buybox",
    "#ctr-midlife-trial-lp-product-buybox",
    "#ctr-midlife-lf-trial-lp-product-buybox",
  ];

  const BUYBOX_PANEL_SELECTOR = [
    "[data-ctr-pdp-ag1-buybox]",
    "[data-ctr-pdp-buybox]",
    "[data-ctr-ih-lp-buybox]",
    "[data-ctr-ed-lp-buybox]",
    "[data-ctr-ml-trial-lp-buybox]",
    "[data-ctr-ml-lf-trial-lp-buybox]",
    "[x-data*='BuyboxPanel']",
  ].join(", ");

  const VISIBILITY_DEBOUNCE_MS = 50;
  const BODY_OFFSET_VAR = "--ctr-sticky-cta-bar-offset";

  function parseConfig(root) {
    try {
      return JSON.parse(root.dataset.config || "{}");
    } catch (_e) {
      return {};
    }
  }

  function getHeaderOffset() {
    const styles = getComputedStyle(document.documentElement);
    const headerHeight = styles.getPropertyValue("--ctr-header-height").trim();
    if (headerHeight) {
      const parsed = parseFloat(headerHeight);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }

    const headerMini = document.querySelector(".header .header__mini");
    if (headerMini?.offsetHeight) return headerMini.offsetHeight;
    return headerMini?.classList.contains("header--nav-collapsed") ? 44 : 56;
  }

  function preferBuyboxPanel(el) {
    if (!el) return null;
    if (el.matches?.(BUYBOX_PANEL_SELECTOR)) return el;
    return el.querySelector?.(BUYBOX_PANEL_SELECTOR) || el;
  }

  function resolveBuyboxRoot(config) {
    const anchor = config.buyboxAnchor?.trim();
    if (anchor) {
      const anchored = document.querySelector(anchor);
      if (anchored) return preferBuyboxPanel(anchored);
    }

    for (const selector of BUYBOX_ROOTS) {
      const el = document.querySelector(selector);
      if (el) return preferBuyboxPanel(el);
    }

    return null;
  }

  function getPackForm(buyboxRoot) {
    const formEl = buyboxRoot?.querySelector(".variant-pack-form");
    if (!formEl || !window.Alpine?.$data) return null;

    try {
      return Alpine.$data(formEl);
    } catch (_e) {
      return null;
    }
  }

  function isOrContains(buyboxRoot, selector) {
    if (!buyboxRoot) return false;
    return Boolean(
      buyboxRoot.matches?.(selector) || buyboxRoot.querySelector?.(selector)
    );
  }

  function getBuyboxEngine(buyboxRoot) {
    if (!buyboxRoot) return null;

    if (
      isOrContains(buyboxRoot, "[data-ctr-pdp-ag1-buybox]") ||
      buyboxRoot.matches?.("#ctr-pdp-product-buybox-v2-ag1") ||
      buyboxRoot.id === "ctr-pdp-product-buybox-v2-ag1" ||
      buyboxRoot.classList?.contains("ctr-pdp-product-buybox-v2-ag1")
    ) {
      return window.ctrPdpBuyboxV2Ag1 || null;
    }

    if (
      isOrContains(buyboxRoot, "[data-ctr-pdp-buybox]") ||
      buyboxRoot.matches?.("#ctr-pdp-product-buybox")
    ) {
      return window.ctrPdpBuybox || null;
    }

    if (
      isOrContains(buyboxRoot, "#ctr-ih-lp-product-buybox") ||
      isOrContains(buyboxRoot, "[data-ctr-ih-lp-buybox]")
    ) {
      return window.ctrIhLpBuybox || null;
    }

    if (
      isOrContains(buyboxRoot, "#ctr-midlife-lf-trial-lp-product-buybox") ||
      isOrContains(buyboxRoot, "[data-ctr-ml-lf-trial-lp-buybox]")
    ) {
      return window.ctrMlLfTrialLpBuybox || null;
    }

    if (
      isOrContains(buyboxRoot, "#ctr-midlife-trial-lp-product-buybox") ||
      isOrContains(buyboxRoot, "[data-ctr-ml-trial-lp-buybox]")
    ) {
      return window.ctrMlTrialLpBuybox || null;
    }

    if (
      isOrContains(buyboxRoot, "#ctr-everyday-dose-lp-product-buybox") ||
      isOrContains(buyboxRoot, "[data-ctr-ed-lp-buybox]")
    ) {
      return window.ctrEdLpBuybox || null;
    }

    if (
      isOrContains(buyboxRoot, "#ctr-lp-product-buybox") ||
      isOrContains(buyboxRoot, ".ctr-lp-product-buybox")
    ) {
      return window.ctrLpBuybox || null;
    }

    // Default PDP (`mfr-product__top`) has no dedicated window engine.
    return null;
  }

  async function triggerLegacyPackFormAtc(buyboxRoot) {
    // Prefer the section submit button so sticky ATC matches the main
    // buybox exactly (cart drawer vs checkout redirect is owned there).
    const btn = buyboxRoot?.querySelector(
      [
        ".variant-pack-form__submit-wrapper button:not([disabled])",
        "button.variant-pack-form__submit:not([disabled])",
      ].join(", ")
    );
    if (btn) {
      btn.click();
      return true;
    }

    const formData = getPackForm(buyboxRoot);
    if (formData?.isFull?.() && typeof formData.addToCart === "function") {
      try {
        const niceTitle =
          buyboxRoot?.dataset?.niceTitle ||
          buyboxRoot?.querySelector?.("[data-nice-title]")?.dataset?.niceTitle ||
          "";
        await formData.addToCart({ niceTitle });
        return true;
      } catch (_e) {
        return false;
      }
    }

    return false;
  }

  function formatMoneyFromCents(cents) {
    if (!cents || cents <= 0) return "";
    const hasFraction = Math.round(cents) % 100 !== 0;
    if (typeof Shopify === "undefined" || !Shopify.formatMoney) {
      return `$${(cents / 100).toFixed(hasFraction ? 2 : 0)}`;
    }
    const format =
      window.theme?.moneyFormat ||
      Shopify.money_format ||
      "${{amount}}";
    return Shopify.formatMoney(cents, format);
  }

  function applyTemplate(template, values) {
    if (!template) return "";
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`[${key}]`, value ?? ""),
      template
    );
  }

  function readVariantLabel(selectorEl) {
    const labelEl = selectorEl.querySelector(
      ".ctr-pdp-variant-pack__pill-label, .ctr-ih-lp-variant-pack__label, .variant-pack-form__selector-label, .variant-pack-form__selector-variant-title, .ctr-pdp-variant-pack__stepper-label, .ctr-ih-lp-variant-pack__stepper-label, .variant-pack-form__selector-title"
    );
    const domLabel = labelEl?.textContent?.trim();
    if (domLabel) return domLabel;

    const alpineEl = selectorEl.querySelector("[x-data]") || selectorEl;
    try {
      const data = Alpine.$data(alpineEl);
      const alpineTitle = data?.variant?.variantTitle || data?.variantData?.variantTitle;
      if (alpineTitle) return String(alpineTitle).trim();
    } catch (_e) {
      /* fall through */
    }

    const xDataAttr = alpineEl.getAttribute("x-data") || "";
    const titleMatch = xDataAttr.match(/"variantTitle"\s*:\s*"((?:\\.|[^"\\])*)"/);
    if (titleMatch?.[1]) {
      return titleMatch[1].replace(/\\"/g, '"').trim();
    }

    return "";
  }

  function readVariantImage(selectorEl) {
    const dataAttr = selectorEl.dataset?.ctrStickySelectorImage?.trim();
    if (dataAttr) return dataAttr;

    const selectorImg = selectorEl.querySelector(
      ".variant-pack-form__selector-image img, .variant-pack-form__selector-image-inner img"
    );
    if (selectorImg) {
      const lazySrc = selectorImg.getAttribute("data-src")?.trim();
      if (lazySrc) return lazySrc;

      const src = selectorImg.currentSrc || selectorImg.getAttribute("src") || "";
      if (src) return src;
    }

    const alpineEl = selectorEl.querySelector("[x-data]") || selectorEl;
    try {
      const data = Alpine.$data(alpineEl);
      const selectorImage = data?.variant?.selectorImage || data?.variant?.selector_image;
      if (selectorImage) return String(selectorImage).trim();

      if (data?.variant?.image) return data.variant.image;
    } catch (_e) {
      /* fall through */
    }

    const xDataAttr = alpineEl.getAttribute("x-data") || "";
    const selectorImageMatch = xDataAttr.match(/"selectorImage"\s*:\s*"((?:\\.|[^"\\])*)"/);
    if (selectorImageMatch?.[1]) {
      return selectorImageMatch[1].replace(/\\"/g, '"').trim();
    }

    const imageMatch = xDataAttr.match(/"image"\s*:\s*"((?:\\.|[^"\\])*)"/);
    if (imageMatch?.[1]) return imageMatch[1].replace(/\\"/g, '"').trim();

    const img = selectorEl.querySelector("img");
    if (!img) return "";
    return img.getAttribute("data-src")?.trim() || img.currentSrc || img.getAttribute("src") || "";
  }

  function readVariantId(selectorEl) {
    const alpineEl = selectorEl.querySelector("[x-data]") || selectorEl;
    try {
      const data = Alpine.$data(alpineEl);
      if (data?.variant?.id) return Number(data.variant.id);
    } catch (_e) {
      /* fall through */
    }

    const input = selectorEl.querySelector("[data-variant-id]");
    if (input?.dataset?.variantId) return Number(input.dataset.variantId);
    return null;
  }

  function readVariantSoldOut(selectorEl) {
    // Mirrors the buybox: `product-top__variant-pack-form` flags variants with
    // `custom.show_out_of_stock` via data-variant-sold-out / .is-sold-out and a
    // disabled sold-out action button.
    if (!selectorEl) return false;
    if (selectorEl.matches?.("[data-variant-sold-out], .is-sold-out")) return true;
    return Boolean(selectorEl.querySelector(".variant-pack-form__selector-action-sold-out"));
  }

  function collectFlavorVariants(buyboxRoot) {
    const selectors = buyboxRoot?.querySelectorAll(".variant-pack-form__selector");
    if (!selectors?.length) return [];

    const variants = [];
    selectors.forEach((selectorEl) => {
      const id = readVariantId(selectorEl);
      if (!id) return;

      variants.push({
        id,
        title: readVariantLabel(selectorEl),
        image: readVariantImage(selectorEl),
        soldOut: readVariantSoldOut(selectorEl),
        element: selectorEl,
      });
    });

    return variants;
  }

  function buildFlavorSummary(formData, flavorVariants) {
    if (!formData?.currentPacks?.length) return "";

    const counts = new Map();
    formData.currentPacks.forEach((packItem) => {
      const variantId = packItem?.variant?.id || packItem?.id;
      if (!variantId) return;
      counts.set(variantId, (counts.get(variantId) || 0) + 1);
    });

    const parts = [];
    counts.forEach((count, variantId) => {
      const match = flavorVariants.find((variant) => variant.id === Number(variantId));
      const title = match?.title || `Variant ${variantId}`;
      parts.push(`${count}x ${title}`);
    });

    return parts.join(" · ");
  }

  const SELECTED_PLAN_CARD_SELECTOR = [
    "[data-ctr-pdp-plan-row].is-selected",
    "[data-ctr-pdp-plan-row][aria-checked='true']",
    "[data-ctr-ih-lp-plan-card].is-selected",
    "[data-ctr-ih-lp-plan-card][aria-checked='true']",
    "[data-ctr-ed-lp-plan-card].is-selected",
    "[data-ctr-ed-lp-plan-card][aria-checked='true']",
  ].join(", ");

  function readSelectedFrequencyInput(buyboxRoot) {
    return buyboxRoot?.querySelector(
      [
        ".variant-pack-form__frequencies input[name='frequency']:checked",
        ".ctr-pdp-product-buybox__skio-engine [name='frequency']:checked",
        ".ctr-ih-lp-product-buybox__skio-engine [name='frequency']:checked",
        ".ctr-lp-product-buybox [name='frequency']:checked",
        ".ctr-everyday-dose-lp-product-buybox [name='frequency']:checked",
      ].join(", ")
    );
  }

  function readShippingDays(buyboxRoot, frequencyPricing) {
    if (frequencyPricing?.deliveryDays) {
      return frequencyPricing.deliveryDays;
    }

    const planCard = readActivePlanCard(buyboxRoot);
    const servingsEl = planCard?.querySelector(
      ".ctr-ih-lp-product-buybox__plan-card-servings, .ctr-pdp-product-buybox__plan-card-servings, .ctr-ed-lp-product-buybox__plan-card-servings"
    );
    const servingsText =
      servingsEl?.textContent?.trim() ||
      planCard?.dataset?.servings?.trim() ||
      "";
    const servingsMatch = servingsText.match(/(\d+)/);
    if (servingsMatch) return parseInt(servingsMatch[1], 10);

    return 0;
  }

  function applyPriceNoteTemplate(template, values) {
    if (!template) return "";

    let result = applyTemplate(template, values);

    if (values.days && !template.includes("[days]")) {
      result = result.replace(/(Ships every\s+)\d+(\s*days)/i, `$1${values.days}$2`);
    }

    return result;
  }

  function resolvePriceNote(buyboxRoot, fallbacks, pricing) {
    const panelEl = readBuyboxPanelElement(buyboxRoot);
    if (panelEl && window.Alpine?.$data) {
      try {
        const note = Alpine.$data(panelEl)?.displayPriceNote;
        if (note?.trim()) return note;
      } catch (_e) {
        /* fall through */
      }
    }

    const priceNoteTemplate = fallbacks.priceNoteTemplate || "";
    if (!priceNoteTemplate) return "";

    const engine = getBuyboxEngine(buyboxRoot);
    const activeInput = readSelectedFrequencyInput(buyboxRoot);
    const frequencyPricing =
      typeof engine?.readFrequencyPricing === "function"
        ? engine.readFrequencyPricing(activeInput)
        : null;
    const days = readShippingDays(buyboxRoot, frequencyPricing || pricing);

    return applyPriceNoteTemplate(priceNoteTemplate, {
      price: pricing.checkoutPrice || pricing.price || "",
      per_day: frequencyPricing?.perDay || pricing.perDay || "",
      days: days ? String(days) : "",
    });
  }

  function readActivePlanCard(buyboxRoot) {
    const selectedCard = buyboxRoot?.querySelector(SELECTED_PLAN_CARD_SELECTOR);
    if (selectedCard) return selectedCard;

    const activeInput = readSelectedFrequencyInput(buyboxRoot);
    const inputId = activeInput?.id;
    if (!inputId) return null;

    return buyboxRoot.querySelector(`[data-frequency-input="${inputId}"]`);
  }

  function readAlpineInputData(input) {
    if (!input || !window.Alpine?.$data) return null;

    try {
      return Alpine.$data(input);
    } catch (_e) {
      return null;
    }
  }

  function formatBoxQuantityPlanLabel(variantLimit, frequencyLabel) {
    const frequency = frequencyLabel?.trim();
    if (!frequency) return "";

    return frequency;
  }

  function readPlanLabel(buyboxRoot, fallback, formData) {
    const planCard = readActivePlanCard(buyboxRoot);
    const activeInput = readSelectedFrequencyInput(buyboxRoot);
    const inputData = readAlpineInputData(activeInput);

    const frequencyLabel =
      planCard?.dataset?.planLabel?.trim() ||
      String(inputData?.frequencyLabel || "").trim() ||
      activeInput?.value?.trim() ||
      "";

    let variantLimit = planCard?.dataset?.variantLimit;
    if (variantLimit == null || variantLimit === "") {
      variantLimit =
        inputData?.variantLimit ??
        formData?.limit ??
        1;
    }

    const label = formatBoxQuantityPlanLabel(variantLimit, frequencyLabel);
    return label || fallback || "";
  }

  function isValidDisplayPrice(price) {
    return typeof price === "string" && /\$[\d]/.test(price);
  }

  function parseDisplayPriceToCents(price) {
    if (!price || typeof price !== "string") return 0;
    const match = price.replace(/,/g, "").match(/\$([\d]+(?:\.\d{2})?)/);
    if (!match) return 0;
    return Math.round(parseFloat(match[1]) * 100);
  }

  function isPerMonthPlanPrice(displayPrice) {
    return /\/mo(?:nth)?/i.test(displayPrice || "");
  }

  function extractPriceSuffix(priceText) {
    const match = String(priceText || "").match(/(\/(?:month|mo))\b/i);
    if (!match) return "";
    return match[1].toLowerCase() === "/mo" ? "/month" : match[1];
  }

  function stripTrailingPriceSuffix(priceText) {
    return String(priceText || "")
      .replace(/\s*\/(?:month|mo)\b/gi, "")
      .trim();
  }

  function formatIntroDiscountDisplay(percent) {
    const parsed = Number(percent);
    if (!Number.isFinite(parsed) || parsed <= 0) return "";
    return (Math.round(parsed * 10) / 10).toFixed(1).replace(/\.0$/, "");
  }

  function formatIntroOfferSuffix(percent) {
    const display = formatIntroDiscountDisplay(percent);
    if (!display) return "";
    return `(${display}% off 1st month)`;
  }

  function resolveStickyCtaDiscountSuffix(inputData, introDiscountPercent, fallbackSuffix = "") {
    const override = String(inputData?.stickyCtaDiscountLabel || "").trim();
    if (override) {
      const numeric = override.replace(/%\s*$/, "").trim();
      if (/^\d+(\.\d+)?$/.test(numeric)) {
        return formatIntroOfferSuffix(Number(numeric));
      }
      return /^\(.*\)$/.test(override) ? override : `(${override})`;
    }

    if (introDiscountPercent > 0) {
      return formatIntroOfferSuffix(introDiscountPercent);
    }

    return fallbackSuffix || "";
  }

  function resolveIntroDiscountPercent(inputData, livePricing, introPriceCents, compareCents) {
    const fromData = Number(inputData?.introDiscountPercent);
    if (Number.isFinite(fromData) && fromData > 0) return fromData;

    const fromLive = Number(livePricing?.introDiscountPercent);
    if (Number.isFinite(fromLive) && fromLive > 0) return fromLive;

    if (introPriceCents > 0 && compareCents > introPriceCents) {
      return Math.round((1 - introPriceCents / compareCents) * 10000) / 100;
    }

    return 0;
  }

  function readLegacyFrequencyPricing(buyboxRoot) {
    const activeInput = readSelectedFrequencyInput(buyboxRoot);
    if (!activeInput) return null;

    const inputData = readAlpineInputData(activeInput);
    const frequencyEl = activeInput.closest(".variant-pack-form__frequency");
    const frequencyPriceEl = frequencyEl?.querySelector(".variant-pack-form__frequency-price");
    const frequencyPriceText = frequencyPriceEl?.textContent?.trim() || "";
    const labelText = String(inputData?.label || "").trim();
    const suffixFromLabel =
      extractPriceSuffix(labelText) || extractPriceSuffix(frequencyPriceText);
    const introAmountText =
      frequencyEl
        ?.querySelector(".variant-pack-form__frequency-intro-price-amount")
        ?.textContent?.trim() || "";
    const scrapedIntroCents = parseDisplayPriceToCents(introAmountText);
    const introPriceCents =
      (Number.isFinite(Number(inputData?.introPriceCents)) && Number(inputData.introPriceCents) > 0
        ? Math.round(Number(inputData.introPriceCents))
        : 0) || scrapedIntroCents || 0;

    if (inputData?.rawPrice != null && inputData.rawPrice > 0) {
      const compareCents = inputData.rawComparePrice || inputData.rawOriginalPrice;
      const compare =
        compareCents && (introPriceCents > 0 || compareCents > inputData.rawPrice)
          ? formatMoneyFromCents(compareCents)
          : null;
      const introDiscountPercent = resolveIntroDiscountPercent(
        inputData,
        null,
        introPriceCents,
        compareCents
      );
      const displayPriceCents = introPriceCents > 0 ? introPriceCents : inputData.rawPrice;

      return {
        price: formatMoneyFromCents(displayPriceCents),
        compare,
        rawPrice: inputData.rawPrice,
        rawComparePrice: compareCents,
        introPriceCents,
        introDiscountPercent,
        isSubscription: Boolean(inputData.sellingPlanID),
        suffix: resolveStickyCtaDiscountSuffix(
          inputData,
          introDiscountPercent,
          suffixFromLabel || (inputData.sellingPlanID ? "/month" : "")
        ),
        discount: introDiscountPercent > 0 ? formatIntroDiscountDisplay(introDiscountPercent) : "",
      };
    }

    if (!isValidDisplayPrice(frequencyPriceText) && !(introPriceCents > 0)) return null;

    const compareEl = frequencyEl?.querySelector(".original-price, .compare-at-price");
    const scrapedCompare = compareEl?.textContent?.trim() || "";
    const introDiscountPercent = resolveIntroDiscountPercent(
      inputData,
      null,
      introPriceCents,
      parseDisplayPriceToCents(scrapedCompare)
    );
    const displayPrice =
      introPriceCents > 0
        ? formatMoneyFromCents(introPriceCents)
        : stripTrailingPriceSuffix(frequencyPriceText) || frequencyPriceText;

    return {
      price: displayPrice,
      compare: isValidDisplayPrice(scrapedCompare) ? scrapedCompare : null,
      rawPrice: parseDisplayPriceToCents(frequencyPriceText),
      introPriceCents,
      introDiscountPercent,
      isSubscription: Boolean(inputData?.sellingPlanID),
      suffix: resolveStickyCtaDiscountSuffix(
        inputData,
        introDiscountPercent,
        suffixFromLabel || (inputData?.sellingPlanID ? "/month" : "")
      ),
      discount: introDiscountPercent > 0 ? formatIntroDiscountDisplay(introDiscountPercent) : "",
    };
  }

  function resolveLiveFrequencyPricing(buyboxRoot) {
    const activeInput = readSelectedFrequencyInput(buyboxRoot);
    const inputData = readAlpineInputData(activeInput);
    const engine = getBuyboxEngine(buyboxRoot);
    const enginePricing =
      typeof engine?.readFrequencyPricing === "function"
        ? engine.readFrequencyPricing(activeInput)
        : null;

    const livePricing = enginePricing || readLegacyFrequencyPricing(buyboxRoot);
    if (!livePricing) return null;

    const introPriceCents =
      (Number.isFinite(Number(livePricing.introPriceCents)) && Number(livePricing.introPriceCents) > 0
        ? Math.round(Number(livePricing.introPriceCents))
        : 0) ||
      (Number.isFinite(Number(inputData?.introPriceCents)) && Number(inputData.introPriceCents) > 0
        ? Math.round(Number(inputData.introPriceCents))
        : 0);
    const compareCents =
      livePricing.rawComparePrice ||
      inputData?.rawComparePrice ||
      inputData?.rawOriginalPrice ||
      parseDisplayPriceToCents(livePricing.compare);
    const introDiscountPercent = resolveIntroDiscountPercent(
      inputData,
      livePricing,
      introPriceCents,
      compareCents
    );

    if (introPriceCents > 0) {
      return {
        ...livePricing,
        price: formatMoneyFromCents(introPriceCents),
        compare: compareCents ? formatMoneyFromCents(compareCents) : livePricing.compare,
        rawComparePrice: compareCents,
        introPriceCents,
        introDiscountPercent,
        suffix: resolveStickyCtaDiscountSuffix(inputData, introDiscountPercent, livePricing.suffix),
        discount: formatIntroDiscountDisplay(introDiscountPercent) || livePricing.discount || "",
        discountPercent: introDiscountPercent || livePricing.discountPercent || 0,
      };
    }

    return livePricing;
  }

  function resolvePriceSuffix(buyboxRoot, fallbacks, livePricing, priceText) {
    const activeInput = readSelectedFrequencyInput(buyboxRoot);
    const inputData = readAlpineInputData(activeInput);
    const stickySuffix = resolveStickyCtaDiscountSuffix(
      inputData,
      livePricing?.introDiscountPercent
    );
    if (stickySuffix) return stickySuffix;

    const frequencyPriceText = activeInput
      ?.closest(".variant-pack-form__frequency")
      ?.querySelector(".variant-pack-form__frequency-price")
      ?.textContent?.trim();
    const isOneTime =
      inputData?.group === "one-time" ||
      /one-time/i.test(String(inputData?.frequencyLabel || activeInput?.value || ""));

    const fromLive =
      livePricing?.suffix ||
      extractPriceSuffix(priceText) ||
      extractPriceSuffix(inputData?.label) ||
      extractPriceSuffix(frequencyPriceText);

    if (fromLive) return fromLive;
    if (isOneTime) return "";
    if (livePricing?.isSubscription || inputData?.sellingPlanID) return "/month";
    if (fallbacks.priceSuffix) return fallbacks.priceSuffix;
    return "/month";
  }

  function readBuyboxPanelElement(buyboxRoot) {
    if (!buyboxRoot) return null;

    if (buyboxRoot.matches?.(BUYBOX_PANEL_SELECTOR)) {
      return buyboxRoot;
    }

    return buyboxRoot.querySelector(BUYBOX_PANEL_SELECTOR);
  }

  function resolveAtcTotalPrice(buyboxRoot, fallbacks) {
    const planCard = readActivePlanCard(buyboxRoot);
    const cardCtaPrice = planCard?.dataset?.ctaPrice?.trim() || "";
    if (isValidDisplayPrice(cardCtaPrice)) return cardCtaPrice;

    const panelEl = readBuyboxPanelElement(buyboxRoot);
    if (panelEl && window.Alpine?.$data) {
      try {
        const panelData = Alpine.$data(panelEl);
        if (panelData?.displayPrice && isValidDisplayPrice(panelData.displayPrice)) {
          return panelData.displayPrice;
        }
      } catch (_e) {
        /* fall through */
      }
    }

    const ctaPriceEl = buyboxRoot?.querySelector(
      ".ctr-pdp-product-buybox__cta-price, .ctr-ih-lp-product-buybox__cta-price"
    );
    const ctaPriceText = ctaPriceEl?.textContent?.trim() || "";
    if (isValidDisplayPrice(ctaPriceText)) return ctaPriceText;

    const pricing = resolveLiveFrequencyPricing(buyboxRoot);

    if (pricing?.price && isValidDisplayPrice(pricing.price)) {
      const cardDisplayPrice = planCard?.dataset?.displayPrice || "";
      const variantLimit = parseInt(planCard?.dataset?.variantLimit || "1", 10);

      if (isPerMonthPlanPrice(cardDisplayPrice) && variantLimit > 1 && pricing.rawPrice > 0) {
        return formatMoneyFromCents(pricing.rawPrice * variantLimit);
      }

      return pricing.price;
    }

    return fallbacks.priceDisplay || "";
  }

  function readTitle(buyboxRoot, fallback) {
    const titleEl = buyboxRoot?.querySelector(
      ".ctr-pdp-product-buybox__title, .ctr-lp-product-buybox__title, .ctr-ih-lp-product-buybox__title, .ctr-everyday-dose-lp-product-buybox__title, .ctr-midlife-trial-lp-product-buybox__title, .ctr-midlife-lf-trial-lp-product-buybox__title, .product-top__title"
    );
    return titleEl?.textContent?.trim() || fallback || "";
  }

  function readPricing(buyboxRoot, fallbacks) {
    const priceEl = buyboxRoot?.querySelector(
      "[data-ctr-pdp-sync-price], [data-ctr-lp-sync-price], .ctr-pdp-product-buybox__price-current, .ctr-lp-product-buybox__price-current, .ctr-ih-lp-product-buybox__price-current"
    );
    const compareEl = buyboxRoot?.querySelector(
      "[data-ctr-pdp-sync-compare], [data-ctr-lp-sync-compare], .ctr-pdp-product-buybox__price-compare, .ctr-lp-product-buybox__price-compare, .product-top__price-old-value"
    );

    const livePricing = resolveLiveFrequencyPricing(buyboxRoot);
    const checkoutPrice = resolveAtcTotalPrice(buyboxRoot, fallbacks);
    const checkoutTotalCents = parseDisplayPriceToCents(checkoutPrice);
    const deliveryDays = readShippingDays(buyboxRoot, livePricing);

    let price =
      livePricing?.price || priceEl?.textContent?.trim() || fallbacks.priceDisplay || "";
    price = stripTrailingPriceSuffix(price) || price;
    const suffix = resolvePriceSuffix(buyboxRoot, fallbacks, livePricing, price);
    const compare =
      livePricing?.compare || compareEl?.textContent?.trim() || fallbacks.compareAtDisplay || "";

    return {
      price,
      compare,
      suffix,
      discount: livePricing?.discount || formatIntroDiscountDisplay(livePricing?.introDiscountPercent || livePricing?.discountPercent) || "",
      deliveryDays,
      checkoutPrice,
      checkoutTotalCents,
      introPriceCents: livePricing?.introPriceCents || 0,
      introDiscountPercent: livePricing?.introDiscountPercent || 0,
    };
  }

  function resolveModeFromSetting(config) {
    const mode = config.modeOverride || "flavor_picker";
    if (mode === "offer_only") return "C";
    if (mode === "compact") return "D";
    if (mode === "summary") return "B";
    return "A";
  }

  function buildStickyCtaLabel(formData, pricing, template) {
    const limit = Number(formData?.limit) || 1;
    const remaining = Math.max(0, limit - (formData?.currentPacks?.length || 0));
    const checkoutPrice = pricing.checkoutPrice || pricing.price;

    if (remaining === 0) {
      return applyTemplate(template || "GO TO CHECKOUT — [price]", {
        price: checkoutPrice,
      });
    }
    if (remaining === 1) return "SELECT 1 MORE FLAVOR";
    return `SELECT ${remaining} MORE FLAVORS`;
  }

  function buildPackSignature(formData) {
    if (!formData) return "";
    const limit = formData.limit ?? "";
    const ids = (formData.currentPacks || [])
      .map((packItem) => packItem?.variant?.id || packItem?.id)
      .filter(Boolean)
      .sort((a, b) => a - b)
      .join(",");
    return `${limit}|${ids}`;
  }

  function flavorEntriesEqual(current, next) {
    if (current.length !== next.length) return false;
    return current.every((entry, index) => {
      const other = next[index];
      return (
        entry.id === other.id &&
        entry.title === other.title &&
        entry.image === other.image &&
        entry.count === other.count
      );
    });
  }

  function isScrolledPastElement(el, headerOffset) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.bottom <= headerOffset;
  }

  function resolveVisibilityTarget(buyboxRoot) {
    if (!buyboxRoot) return null;

    const candidates = [
      ".ctr-pdp-product-buybox__cta-block",
      ".ctr-ih-lp-product-buybox__cta-block",
      ".ctr-everyday-dose-lp-product-buybox__cta-block",
      ".ctr-midlife-trial-lp-product-buybox__cta-block",
      ".ctr-midlife-lf-trial-lp-product-buybox__cta-block",
      ".ctr-lp-product-buybox__purchase-card .ctr-lp-purchase-card__cta",
      "[data-ctr-pdp-trigger-atc]",
      "[data-ctr-ih-lp-trigger-atc]",
      "[data-ctr-ed-lp-trigger-atc]",
      "[data-ctr-ml-trial-lp-trigger-atc]",
      "[data-ctr-ml-lf-trial-lp-trigger-atc]",
      "[data-ctr-lp-trigger-atc]",
      ".variant-pack-form__submit-wrapper",
    ];

    for (const selector of candidates) {
      const el = buyboxRoot.querySelector(selector);
      if (!el) continue;

      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") continue;

      return el;
    }

    return buyboxRoot;
  }

  function updateStickyBarVisibility(component, target, headerOffset) {
    if (!target) return;

    component.isVisible = isScrolledPastElement(target, headerOffset);
    updateBodyOffset(component.$el, component.isVisible);
  }

  function updateBodyOffset(barEl, isVisible) {
    if (!isVisible) {
      document.documentElement.style.setProperty(BODY_OFFSET_VAR, "0px");
      return;
    }

    const height = barEl?.offsetHeight || 0;
    document.documentElement.style.setProperty(BODY_OFFSET_VAR, `${height}px`);
  }

  function registerAlpineComponent() {
    window._mfrAlpineRegistered = window._mfrAlpineRegistered || {};
    if (window._mfrAlpineRegistered.ctrStickyCtaBar) return;

    Alpine.data("ctrStickyCtaBar", () => ({
      config: {},
      buyboxRoot: null,
      packRevision: 0,
      isVisible: false,
      isInitialized: false,
      isDropdownOpen: false,
      resolvedMode: "A",
      flavorVariants: [],
      selectedFlavorEntries: [],
      displayTitle: "",
      displayPlan: "",
      displayPrice: "",
      displayCompare: "",
      displayPriceSuffix: "",
      displayPriceNote: "",
      flavorSummary: "",
      ctaLabel: "",
      offerCtaLabel: "",
      isCheckoutReady: false,
      dropdownTriggerLabel: "Select flavors",
      _visibilityTimer: null,
      _visibilityObserver: null,
      _visibilityTarget: null,
      _packPollTimer: null,
      _syncTimer: null,
      _lastPackSignature: "",
      _lastPlanLabel: "",
      _lastPricingKey: "",
      _onVisibilityScroll: null,

      init() {
        this.config = parseConfig(this.$el);
        this.resolvedMode = resolveModeFromSetting(this.config);
        this.dropdownTriggerLabel = this.config.flavorDropdownLabel || "Select flavors";
        this.applyFallbacks();
        this.bindBuybox();
        this.initVisibility();
        this.$watch("isVisible", (visible) => {
          if (!visible) this.closeDropdown();
        });
        this.isInitialized = true;
        this.syncFromBuybox();
      },

      applyFallbacks() {
        const fallbacks = this.config.fallbacks || {};
        this.displayTitle = fallbacks.productTitle || "";
        this.displayPlan = fallbacks.planLabel || "";
        this.displayPrice = fallbacks.priceDisplay || "";
        this.displayCompare = fallbacks.compareAtDisplay || "";
        this.displayPriceSuffix = fallbacks.priceSuffix || "/month";
        this.offerCtaLabel = applyTemplate(this.config.offerCtaTemplate || "GET [discount]% OFF — [price]", {
          discount: "40",
          price: fallbacks.priceDisplay || "$47.40",
        });
        this.ctaLabel = this.config.checkoutCtaTemplate || "GO TO CHECKOUT — [price]";
      },

      bindBuybox(attempt = 0) {
        this.buyboxRoot = resolveBuyboxRoot(this.config);
        if (!this.buyboxRoot) {
          if (attempt < 20) {
            window.setTimeout(() => this.bindBuybox(attempt + 1), 500);
          }
          return;
        }

        const buyboxRoot = this.buyboxRoot;
        const selectors = buyboxRoot.querySelector(".variant-pack-form__selectors");
        if (selectors) {
          const observer = new MutationObserver(() => this.scheduleSyncFromBuybox());
          observer.observe(selectors, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ["class"],
          });
        }

        const frequencies = buyboxRoot.querySelector(".variant-pack-form__frequencies");
        frequencies?.addEventListener("change", (event) => {
          if (event.target?.matches?.("[name='frequency']")) {
            this.scheduleSyncFromBuybox(true);
          }
        });

        this._packPollTimer = window.setInterval(() => {
          const formData = this.getPackForm();
          const signature = buildPackSignature(formData);
          if (signature !== this._lastPackSignature) {
            this.scheduleSyncFromBuybox(true);
          }
        }, 400);
      },

      scheduleSyncFromBuybox(force = false) {
        if (force) {
          window.clearTimeout(this._syncTimer);
          this.syncFromBuybox();
          return;
        }

        window.clearTimeout(this._syncTimer);
        this._syncTimer = window.setTimeout(() => {
          this.syncFromBuybox();
        }, 32);
      },

      getPackForm() {
        return getPackForm(this.buyboxRoot);
      },

      initVisibility() {
        const buyboxRoot = this.buyboxRoot || resolveBuyboxRoot(this.config);
        const target = resolveVisibilityTarget(buyboxRoot);
        if (!target) {
          window.setTimeout(() => this.initVisibility(), 500);
          return;
        }

        if (this._visibilityObserver) {
          this._visibilityObserver.disconnect();
          this._visibilityObserver = null;
        }

        if (this._onVisibilityScroll) {
          window.removeEventListener("scroll", this._onVisibilityScroll);
          window.removeEventListener("resize", this._onVisibilityScroll);
        }

        const headerOffset = getHeaderOffset();
        this._visibilityTarget = target;

        this._onVisibilityScroll = () => {
          window.clearTimeout(this._visibilityTimer);
          this._visibilityTimer = window.setTimeout(() => {
            updateStickyBarVisibility(this, this._visibilityTarget, getHeaderOffset());
          }, VISIBILITY_DEBOUNCE_MS);
        };

        updateStickyBarVisibility(this, target, headerOffset);
        window.addEventListener("scroll", this._onVisibilityScroll, { passive: true });
        window.addEventListener("resize", this._onVisibilityScroll);
      },

      syncFromBuybox() {
        if (!this.buyboxRoot) {
          this.buyboxRoot = resolveBuyboxRoot(this.config);
        }
        if (!this.buyboxRoot) return;

        const fallbacks = this.config.fallbacks || {};
        const formData = this.getPackForm();
        const packSignature = buildPackSignature(formData);
        const packChanged = packSignature !== this._lastPackSignature;
        const pricing = readPricing(this.buyboxRoot, fallbacks);
        const pricingKey = `${pricing.price}|${pricing.compare}|${pricing.checkoutPrice}|${pricing.deliveryDays}`;
        const pricingChanged = pricingKey !== this._lastPricingKey;

        if (packChanged || !this.flavorVariants.length) {
          this.flavorVariants = collectFlavorVariants(this.buyboxRoot);
        }

        this.displayTitle = readTitle(this.buyboxRoot, fallbacks.productTitle);
        this.resolvedMode = resolveModeFromSetting(this.config);

        if (this.resolvedMode === "D") {
          this.displayPrice = fallbacks.priceDisplay || pricing.price;
          this.displayCompare = fallbacks.compareAtDisplay || pricing.compare || "";
          this.displayPriceSuffix = "";
        } else {
          this.displayPrice = pricing.price;
          this.displayCompare =
            this.resolvedMode === "A" || this.resolvedMode === "B"
              ? pricing.compare
              : "";
          this.displayPriceSuffix = this.resolvedMode === "B" ? "" : pricing.suffix;
        }

        const nextPlanLabel = readPlanLabel(this.buyboxRoot, fallbacks.planLabel, formData);
        if (nextPlanLabel !== this._lastPlanLabel) {
          this.displayPlan = nextPlanLabel;
          this._lastPlanLabel = nextPlanLabel;
        }

        const nextFlavorSummary = buildFlavorSummary(formData, this.flavorVariants);
        if (nextFlavorSummary !== this.flavorSummary) {
          this.flavorSummary = nextFlavorSummary;
        }

        if (packChanged) {
          const nextEntries = this.flavorVariants
            .filter((variant) => this.packCountForForm(formData, variant.id) > 0)
            .map((variant) => ({
              id: variant.id,
              title: variant.title,
              image: variant.image,
              count: this.packCountForForm(formData, variant.id),
            }));

          if (!flavorEntriesEqual(this.selectedFlavorEntries, nextEntries)) {
            this.selectedFlavorEntries = nextEntries;
          }

          this._lastPackSignature = packSignature;
          this.packRevision += 1;
        }

        this.isCheckoutReady = Boolean(formData?.isFull?.());
        this.ctaLabel = buildStickyCtaLabel(
          formData,
          pricing,
          this.config.checkoutCtaTemplate
        );

        this.offerCtaLabel = applyTemplate(this.config.offerCtaTemplate || "", {
          discount: pricing.discount || "40",
          price: pricing.price || fallbacks.priceDisplay || "",
        });

        if (pricingChanged || !this.displayPriceNote) {
          this.displayPriceNote = resolvePriceNote(this.buyboxRoot, fallbacks, pricing);
        }

        this._lastPricingKey = pricingKey;
      },

      packCountForForm(formData, variantId) {
        return formData?.isInPack?.(variantId) || 0;
      },

      packCount(variantId) {
        void this.packRevision;
        const formData = this.getPackForm();
        return formData?.isInPack?.(variantId) || 0;
      },

      isPackFull() {
        const formData = this.getPackForm();
        return formData?.isFull?.() ?? false;
      },

      packAdd(variant) {
        if (variant?.soldOut) return;

        const formData = this.getPackForm();
        if (!formData || formData.isFull?.()) return;

        const packVariant = this.resolvePackVariant(variant);
        if (!packVariant) return;

        if (formData.isSingleSelect?.()) {
          formData.toggleSingleSelection(packVariant);
        } else {
          formData.addToPack(packVariant);
        }

        this.scheduleSyncFromBuybox(true);
      },

      packRemove(variant) {
        const formData = this.getPackForm();
        if (!formData) return;

        const packVariant = this.resolvePackVariant(variant);
        if (!packVariant) return;

        formData.decreaseFromPack?.(packVariant);
        this.scheduleSyncFromBuybox(true);
      },

      resolvePackVariant(variant) {
        if (!variant?.element) return null;

        try {
          const cardData = Alpine.$data(
            variant.element.querySelector("[x-data]") || variant.element
          );
          if (cardData?.variant) return cardData.variant;
        } catch (_e) {
          /* fall through */
        }

        return { id: variant.id, variantTitle: variant.title, image: variant.image };
      },

      toggleDropdown() {
        this.isDropdownOpen = !this.isDropdownOpen;
      },

      closeDropdown() {
        this.isDropdownOpen = false;
      },

      scrollToBuybox() {
        const engine = getBuyboxEngine(this.buyboxRoot);
        if (typeof engine?.scrollToBuybox === "function") {
          return engine.scrollToBuybox();
        }

        if (!this.buyboxRoot) return false;
        const top =
          this.buyboxRoot.getBoundingClientRect().top +
          window.scrollY -
          getHeaderOffset();
        window.scrollTo({ top, behavior: "smooth" });
        return true;
      },

      scrollToFlavorSelector() {
        const flavorArea =
          this.buyboxRoot?.querySelector(
            ".ctr-pdp-variant-pack__selectors, .variant-pack-form__selectors, .ctr-ih-lp-variant-pack__selectors"
          ) || this.buyboxRoot;

        if (!flavorArea) return this.scrollToBuybox();

        const top =
          flavorArea.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
        window.scrollTo({ top, behavior: "smooth" });
        return true;
      },

      async triggerCheckout() {
        const engine = getBuyboxEngine(this.buyboxRoot);
        if (typeof engine?.triggerBuyboxAtc === "function") {
          return engine.triggerBuyboxAtc();
        }
        return triggerLegacyPackFormAtc(this.buyboxRoot);
      },

      onPrimaryCtaClick() {
        if (this.resolvedMode === "A" && !this.isCheckoutReady) {
          if (!this.isDropdownOpen) {
            this.isDropdownOpen = true;
            return;
          }
          this.scrollToFlavorSelector();
          return;
        }

        if (!this.isCheckoutReady) {
          this.scrollToFlavorSelector();
          return;
        }

        this.triggerCheckout();
      },

      onOfferCtaClick() {
        if (this.buyboxRoot?.matches?.("#ctr-midlife-lf-trial-lp-product-buybox, [data-ctr-ml-lf-trial-lp-buybox], #ctr-midlife-trial-lp-product-buybox, [data-ctr-ml-trial-lp-buybox]")) {
          this.triggerCheckout();
          return;
        }

        if (this.buyboxRoot?.matches?.("#ctr-everyday-dose-lp-product-buybox, [data-ctr-ed-lp-buybox]")) {
          const edEngine = window.ctrEdLpBuybox;
          if (typeof edEngine?.scrollToBuybox === "function") {
            edEngine.scrollToBuybox();
            return;
          }
        }

        this.scrollToBuybox();
      },

      destroy() {
        this._visibilityObserver?.disconnect();
        window.clearTimeout(this._visibilityTimer);
        window.clearTimeout(this._syncTimer);
        window.clearInterval(this._packPollTimer);
      },
    }));

    window._mfrAlpineRegistered.ctrStickyCtaBar = true;
  }

  if (window.Alpine) {
    registerAlpineComponent();
  } else {
    document.addEventListener("alpine:init", registerAlpineComponent);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.querySelector?.("[data-ctr-sticky-cta-bar]") && window.Alpine) {
      registerAlpineComponent();
    }
  });
})();
